// /src/lib/analytics.js
// Privacy-Preserving Analytics Service for OurNet
//
// Features:
// - No user identification (session-based tracking only)
// - Silent failures (never breaks UX)
// - Minimal performance impact
// - Automatic session tracking
//
// Usage:
//   import { analytics } from '../lib/analytics';
//   analytics.track('article_read', { content_type: 'magazine' });

// Get analytics endpoint from environment or use default
const ANALYTICS_ENDPOINT = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/analytics-event`
  : null;

// Critical events for Phase 1 - only these are sent to backend
const CRITICAL_EVENTS = [
  'session_start',
  'session_end',
  'registration',
  'group_joined',
  'article_read',
  'content_published',
];

/**
 * OurNetAnalytics - Privacy-preserving analytics class
 */
class OurNetAnalytics {
  constructor() {
    this.sessionStart = Date.now();
    this.actionsCount = 0;
    this.scrollDepth = 0;
    this.initialized = false;
    this.localityId = null;

    // Don't auto-initialize - let the app control when to start
  }

  /**
   * Initialize analytics tracking
   * Call this once when the app loads
   */
  init() {
    if (this.initialized || !ANALYTICS_ENDPOINT) {
      return;
    }

    this.initialized = true;
    this.initScrollTracking();
    this.initVisibilityTracking();
    this.trackSessionStart();

    console.debug('[Analytics] Initialized');
  }

  /**
   * Set the user's locality ID for geographic context
   * @param {string|null} localityId - UUID of the locality
   */
  setLocality(localityId) {
    this.localityId = localityId;
  }

  /**
   * Track an analytics event
   * @param {string} eventType - Event type (e.g., 'article_read', 'group_joined')
   * @param {object} metadata - Additional event data
   */
  track(eventType, metadata = {}) {
    if (!this.initialized || !ANALYTICS_ENDPOINT) {
      return;
    }

    this.actionsCount++;

    // Only send critical events to backend (Phase 1)
    if (!CRITICAL_EVENTS.includes(eventType)) {
      console.debug(`[Analytics] Non-critical event ignored: ${eventType}`);
      return;
    }

    // Build event payload
    const payload = {
      event_type: eventType,
      metadata: {
        ...metadata,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      },
      device_type: this.getDeviceType(),
      locality_id: this.localityId,
    };

    // Send to backend (fire and forget)
    this.sendEvent(payload);
  }

  /**
   * Send event to analytics endpoint
   * @param {object} payload - Event payload
   */
  async sendEvent(payload) {
    try {
      await fetch(ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        // Use keepalive for session_end events (page unload)
        keepalive: payload.event_type === 'session_end',
      });
    } catch (error) {
      // Silent failure - never break user experience
      console.debug('[Analytics] Event send failed:', error.message);
    }
  }

  /**
   * Track session start
   */
  trackSessionStart() {
    const entryPoint = this.getEntryPoint();
    const isNewSession = !sessionStorage.getItem('ournet_analytics_session');

    // Mark session as existing
    sessionStorage.setItem('ournet_analytics_session', 'true');

    this.track('session_start', {
      entry_point: entryPoint,
      is_new_session: isNewSession,
      referrer: document.referrer || null,
    });
  }

  /**
   * Track session end (called on page unload or visibility change)
   */
  trackSessionEnd() {
    const durationMinutes = Math.floor((Date.now() - this.sessionStart) / 60000);

    this.track('session_end', {
      duration_minutes: durationMinutes,
      actions_taken: this.actionsCount,
      max_scroll_depth: this.scrollDepth,
    });
  }

  /**
   * Initialize scroll depth tracking
   */
  initScrollTracking() {
    let maxScroll = 0;
    let ticking = false;

    const updateScrollDepth = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);
        if (scrollPercent > maxScroll) {
          maxScroll = scrollPercent;
          this.scrollDepth = maxScroll;
        }
      }
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollDepth);
        ticking = true;
      }
    }, { passive: true });
  }

  /**
   * Initialize visibility change tracking (for session end)
   */
  initVisibilityTracking() {
    // Track when user leaves the page
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackSessionEnd();
      }
    });

    // Also track on beforeunload for browsers that don't fire visibilitychange
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });
  }

  /**
   * Get device type based on viewport width
   * @returns {'mobile'|'tablet'|'desktop'}
   */
  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Get entry point (how user arrived)
   * @returns {string}
   */
  getEntryPoint() {
    const referrer = document.referrer;
    const url = window.location.href;

    if (!referrer) return 'direct';
    if (referrer.includes(window.location.hostname)) return 'internal';
    if (url.includes('/invite/')) return 'group_invite';
    if (url.includes('/join/')) return 'group_join_link'; // shared join link (Invitations Phase 3, #54)
    if (url.includes('/article/')) return 'article_link';
    if (referrer.includes('google') || referrer.includes('bing')) return 'search';
    if (referrer.includes('facebook') || referrer.includes('twitter') || referrer.includes('linkedin')) return 'social';
    return 'referral';
  }

  // ==========================================
  // Convenience methods for common events
  // ==========================================

  /**
   * Track article read
   * @param {object} article - Article object with id, article_type, etc.
   * @param {number} readDurationSeconds - How long user spent reading
   * @param {number} scrollDepthPercent - How far user scrolled
   */
  trackArticleRead(article, readDurationSeconds, scrollDepthPercent) {
    this.track('article_read', {
      content_type: article.article_type || 'article',
      read_duration_seconds: readDurationSeconds,
      scroll_depth_percent: scrollDepthPercent,
      completed_read: readDurationSeconds > 30 && scrollDepthPercent > 70,
    });
  }

  /**
   * Track group joined
   * @param {object} group - Group/community object
   * @param {string} discoveryMethod - How user found the group
   */
  trackGroupJoined(group, discoveryMethod = 'unknown') {
    this.track('group_joined', {
      group_type: group.community_type || 'community',
      discovery_method: discoveryMethod,
      is_first_group: false, // TODO: Check if this is user's first group
    });
  }

  /**
   * Track content published
   * @param {object} content - The published content
   */
  trackContentPublished(content) {
    this.track('content_published', {
      content_type: content.article_type || 'article',
      is_first_post: false, // TODO: Check if this is user's first post
    });
  }

  /**
   * Track registration completed
   * @param {string} triggerContext - What triggered the registration
   */
  trackRegistration(triggerContext = 'direct') {
    this.track('registration', {
      trigger_context: triggerContext,
      journey_duration_minutes: Math.floor((Date.now() - this.sessionStart) / 60000),
      actions_before_registration: this.actionsCount,
    });
  }
}

// Create singleton instance
const analytics = new OurNetAnalytics();

// Export singleton and class (for testing)
export { analytics, OurNetAnalytics };
export default analytics;
